import { useCallback, useEffect, useState } from "react";
import { Alert, Button, FormGroup, Input, Label } from "reactstrap";
import { unwrapPayload } from "../../utilities";
import DateTimeText from "../../../../../components/DateTimeText";

export function WorkerHistorySection
(
    {
        searchWorkerHistory
    }
    :
    {
        searchWorkerHistory: (values: any) => Promise<any>;
    }
)
{
    const [pageSize, setPageSize] = useState(50);
    const [pageNumber, setPageNumber] = useState(1);
    const [eventType, setEventType] = useState('');
    const [status, setStatus] = useState('');
    const [result, setResult] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadHistory = useCallback(async (requestedPageNumber = pageNumber) =>
    {
        const safePageNumber = Math.max(requestedPageNumber || 1, 1);
        const safePageSize = Math.min(Math.max(pageSize || 50, 1), 500);

        setError(null);
        setIsLoading(true);

        try
        {
            const response = await searchWorkerHistory({
                pageNumber: safePageNumber,
                pageSize: safePageSize,
                eventTypes: eventType ? [eventType] : [],
                statuses: status ? [status] : []
            });

            const payload = unwrapPayload(response);
            setResult(payload);
            setPageNumber(payload?.pageNumber || safePageNumber);
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Pobieranie historii workera zakończyło się błędem.');
        }
        finally
        {
            setIsLoading(false);
        }
    }, [eventType, pageNumber, pageSize, searchWorkerHistory, status]);

    useEffect(() =>
    {
        loadHistory(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () =>
    {
        setPageNumber(1);
        loadHistory(1);
    };

    const changePage = (nextPageNumber: number) =>
    {
        setPageNumber(nextPageNumber);
        loadHistory(nextPageNumber);
    };

    const items = result?.items || [];
    const totalCount = result?.totalCount || 0;
    const totalPages = result?.totalPages || 0;
    const currentPageNumber = result?.pageNumber || pageNumber;
    const hasPreviousPage = result?.hasPreviousPage === true;
    const hasNextPage = result?.hasNextPage === true;
    const aggregationKeyCellStyle = {
        maxWidth: '500px',
        whiteSpace: 'normal' as const,
        overflowWrap: 'anywhere' as const,
        wordBreak: 'break-all' as const
    };

    return (
        <section className="mt-5">
            <h5>Historia eksportu</h5>

            {error && <Alert color="danger">{error}</Alert>}

            <div className="row">
                <div className="col-12 col-xl-3">
                    <FormGroup>
                        <Label>Wpisów na stronie</Label>
                        <Input
                            type="number"
                            min={1}
                            max={500}
                            value={pageSize}
                            onChange={event => setPageSize(Number.parseInt(event.target.value, 10) || 50)}
                        />
                    </FormGroup>
                </div>

                <div className="col-12 col-xl-3">
                    <FormGroup>
                        <Label>Typ zdarzenia</Label>
                        <Input
                            type="select"
                            value={eventType}
                            onChange={event => setEventType(event.target.value)}
                        >
                            <option value="">Wszystkie</option>
                            <option value="NoWork">NoWork</option>
                            <option value="RunSummary">RunSummary</option>
                            <option value="Error">Error</option>
                        </Input>
                    </FormGroup>
                </div>

                <div className="col-12 col-xl-3">
                    <FormGroup>
                        <Label>Status</Label>
                        <Input
                            type="select"
                            value={status}
                            onChange={event => setStatus(event.target.value)}
                        >
                            <option value="">Wszystkie</option>
                            <option value="Success">Success</option>
                            <option value="Warning">Warning</option>
                            <option value="Failed">Failed</option>
                        </Input>
                    </FormGroup>
                </div>

                <div className="col-12 col-xl-3 d-flex align-items-end justify-content-xl-end">
                    <Button
                        type="button"
                        color="secondary"
                        className="mb-3"
                        disabled={isLoading}
                        onClick={applyFilters}
                    >
                        Odśwież pierwszą stronę
                    </Button>
                </div>
            </div>

            {result && (
                <div className="mt-3">
                    {result.success === false && result.message && (
                        <Alert color="warning">
                            {result.message}
                        </Alert>
                    )}

                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                        <div className="text-muted mb-2">
                            {totalCount === 0
                                ? 'Brak wpisów historii eksportu.'
                                : `Strona ${currentPageNumber} z ${totalPages}, razem ${totalCount} wpisów.`}
                        </div>
                        <div className="mb-2">
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                className="mr-2"
                                disabled={isLoading || !hasPreviousPage}
                                onClick={() => changePage(currentPageNumber - 1)}
                            >
                                Poprzednia
                            </Button>
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                disabled={isLoading || !hasNextPage}
                                onClick={() => changePage(currentPageNumber + 1)}
                            >
                                Następna
                            </Button>
                        </div>
                    </div>

                    {items.length === 0 ? (
                        <Alert color="info">
                            Brak wpisów historii eksportu.
                        </Alert>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0">
                                <thead>
                                    <tr>
                                        <th>Ostatnio</th>
                                        <th>Od</th>
                                        <th>Typ</th>
                                        <th>Status</th>
                                        <th>Klucz</th>
                                        <th>Wystąpienia</th>
                                        <th>Prepared</th>
                                        <th>Sent</th>
                                        <th>Skipped</th>
                                        <th>Failed</th>
                                        <th>Komunikat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item: any) => (
                                        <tr key={item.id}>
                                            <td className="text-nowrap"><DateTimeText value={item.lastAt} /></td>
                                            <td className="text-nowrap"><DateTimeText value={item.firstAt} /></td>
                                            <td>{item.eventType}</td>
                                            <td>{item.status}</td>
                                            <td style={aggregationKeyCellStyle}><code className="d-block">{item.aggregationKey}</code></td>
                                            <td>{item.occurrenceCount}</td>
                                            <td>{item.preparedCount}</td>
                                            <td>{item.sentCount}</td>
                                            <td>{item.skippedCount}</td>
                                            <td>{item.failedCount}</td>
                                            <td>{item.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <details className="mt-4">
                        <summary>Raw JSON</summary>
                        <pre className="mt-3">{JSON.stringify(result, null, 2)}</pre>
                    </details>
                </div>
            )}
        </section>
    );
}
